import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {prescriptionCategories} from './legendConstants';
import {getCheckedTypes} from './legendUtils';

export type TLegendSlice = {
  isPluAvailabilityLayerShown: boolean;
  isZoningLayerShown: boolean;
  isFloorAreaPrecisionLayerShown: boolean;
  isZoningLayerAvailable: boolean;
  checkedZonings: string[] | 'all';
  isLandUnitsLayerShown: boolean;
  landUnitsFloorAreaThreshold: number;
  checkedPrescriptions: string[];
  availablePrescriptionCategories: string[];
  bufferedState: null | {
    checkedPrescriptions: string[];
    isPluAvailabilityLayerShown: boolean;
    isZoningLayerShown: boolean;
  };
};

const initialState: TLegendSlice = {
  isPluAvailabilityLayerShown: false,
  isZoningLayerShown: true,
  isFloorAreaPrecisionLayerShown: false,
  isZoningLayerAvailable: false,
  checkedZonings: 'all',
  isLandUnitsLayerShown: false,
  landUnitsFloorAreaThreshold: 1000,
  checkedPrescriptions: getCheckedTypes(prescriptionCategories),
  availablePrescriptionCategories: [],
  bufferedState: null,
};

const legendSlice = createSlice({
  name: 'legend',
  initialState,
  reducers: {
    togglePluAvailabilityLayer: state => {
      const nextState = !state.isPluAvailabilityLayerShown;
      state.isPluAvailabilityLayerShown = nextState;
      state.isFloorAreaPrecisionLayerShown = false;
    },
    toggleFloorAreaPrecisionLayer: state => {
      const nextState = !state.isFloorAreaPrecisionLayerShown;
      state.isFloorAreaPrecisionLayerShown = nextState;
      state.isPluAvailabilityLayerShown = false;
    },
    enableZoningLayer: state => {
      state.isZoningLayerAvailable = true;
    },
    disableZoningLayer: state => {
      state.isZoningLayerAvailable = false;
    },
    checkZonings: (state, {payload}: PayloadAction<string[]>) => {
      state.checkedZonings = payload;
    },
    checkAllZonings: state => {
      state.checkedZonings = 'all';
    },
    toggleLandUnitsLayer: state => {
      state.isLandUnitsLayerShown = !state.isLandUnitsLayerShown;
    },
    showLandUnitsLayer: state => {
      state.isLandUnitsLayerShown = true;
    },
    hideLandUnitsLayer: state => {
      state.isLandUnitsLayerShown = false;
    },
    setLandUnitsFloorAreaThreshold: (
      state,
      {payload}: PayloadAction<number>,
    ) => {
      state.landUnitsFloorAreaThreshold = payload;
    },
    addCheckedPrescriptions: (state, {payload}: PayloadAction<string[]>) => {
      state.checkedPrescriptions.push(...payload);
    },
    removeCheckedPrescriptions: (state, {payload}: PayloadAction<string[]>) => {
      state.checkedPrescriptions = state.checkedPrescriptions.filter(
        t => !payload.includes(t),
      );
    },
    setPrescriptionCategoriesAvailability: (
      state,
      {payload}: PayloadAction<string[]>,
    ) => {
      state.availablePrescriptionCategories = payload;
    },
    hideLegendLayers: state => {
      state.bufferedState = {
        checkedPrescriptions: state.checkedPrescriptions,
        isPluAvailabilityLayerShown: state.isPluAvailabilityLayerShown,
        isZoningLayerShown: state.isZoningLayerShown,
      };
      state.checkedPrescriptions = [];
      state.isPluAvailabilityLayerShown = false;
      state.isZoningLayerShown = false;
    },
    restoreLegendLayers: state => {
      if (state.bufferedState) {
        state.checkedPrescriptions = state.bufferedState.checkedPrescriptions;
        state.isPluAvailabilityLayerShown =
          state.bufferedState.isPluAvailabilityLayerShown;
        state.isZoningLayerShown = state.bufferedState.isZoningLayerShown;
      }
      state.bufferedState = null;
    },
  },
});

export const {
  togglePluAvailabilityLayer,
  toggleFloorAreaPrecisionLayer,
  addCheckedPrescriptions,
  removeCheckedPrescriptions,
  enableZoningLayer,
  disableZoningLayer,
  checkZonings,
  checkAllZonings,
  toggleLandUnitsLayer,
  showLandUnitsLayer,
  hideLandUnitsLayer,
  setLandUnitsFloorAreaThreshold,
  setPrescriptionCategoriesAvailability,
  hideLegendLayers,
  restoreLegendLayers,
} = legendSlice.actions;

export default legendSlice.reducer;
